import { DatePipe, NgClass } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  InjectionToken,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Column, FormField } from '@fsf/types';
import { LARAVEL_ENDPOINT, LaravelApi, LaravelResult } from '@fsf/util';
import { _, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { query } from '@vortechron/query-builder-ts';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { finalize } from 'rxjs';
import * as z from 'zod';
import { provideComponentDefaultTranslations } from '../../util/defaults-translation-handler';
import translations from './i18n';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type UserTableSchema = z.ZodObject<any>;

export const USER_API = new InjectionToken<LaravelApi<Record<string, unknown>>>(
  'laravel.api.users',
);

export const USER_SCHEMA = new InjectionToken<UserTableSchema>(
  'user.table.schema',
);

_([
  'label.addUser',
  'label.cancel',
  'label.clearFilter',
  'label.delete',
  'label.errorDeleting',
  'label.errorHeader',
  'label.invalidData',
  'label.onboardingMailError',
  'label.onboardingMailSent',
  'label.save',
  'label.sendOnboardingMail',
  'label.successfullyDeleted',
  'label.successfullyDeletedHeader',
  'label.successHeader',
  'placeholder.search',
  'user.create',
  'user.created',
  'user.createFailed',
  'user.deleteConfirmMessage',
  'user.deleteHeader',
  'user.edit',
  'user.updated',
  'user.updateFailed',
]);

@Component({
  selector: 'ui-user-table',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    TagModule,
    MultiSelectModule,
    SelectModule,
    TranslatePipe,
    NgClass,
    ToggleSwitchModule,
    ChipModule,
    DatePickerModule,
    ProgressBarModule,
    DatePipe,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    ButtonModule,
    DialogModule,
    ConfirmDialogModule,
    ToastModule,
    TooltipModule,
  ],
  templateUrl: './user-table.component.html',
  styleUrl: './user-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    MessageService,
    ConfirmationService,
    provideComponentDefaultTranslations(translations),
  ],
})
export class UserTableComponent {
  #api = inject(USER_API);
  #schema = inject(USER_SCHEMA);
  #router = inject(Router);
  #fb = inject(FormBuilder);
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translateService = inject(TranslateService);
  #http = inject(HttpClient);
  #endpoint = inject(LARAVEL_ENDPOINT);

  isLoading = signal<boolean>(false);
  totalRecords = signal<number>(0);
  users = signal<Record<string, unknown>[]>([]);
  tagOptions = signal<Record<string, string[]>>({});
  selectOptions = signal<
    Record<string, { label: string; value: string | number }[]>
  >({});

  activeFilters = signal<{ field: string; label: string; value: string }[]>([]);

  currentUser: Record<string, unknown> | null = null;
  userPopupVisible = false;

  cols: Column[] = this.#schema.meta()?.columns ?? [];
  filterFields: string[] = this.#schema.meta()?.globalFilterFields ?? [];
  formFields: FormField[] = this.#schema.meta()?.formFields ?? [];
  userForm: FormGroup = this.#buildForm();

  constructor() {
    this.#loadTagOptions();
    this.#loadSelectOptions();
  }

  #lastLazyEvent: TableLazyLoadEvent | null = null;

  #collectedValues = signal<Record<string, string[]>>({});

  filterOptions = computed(() => {
    const collected = this.#collectedValues();
    const options: Record<string, { label: string; value: string }[]> = {};

    for (const col of this.cols) {
      if (col.matchMode === 'in') {
        if (col.textOptions?.length) {
          options[col.field as string] = col.textOptions.map((v) => ({
            label: v,
            value: v,
          }));
        } else {
          const values = collected[col.field as string] ?? [];
          options[col.field as string] = values.map((v) => ({
            label: v,
            value: v,
          }));
        }
      }
    }

    return options;
  });

  #buildForm(): FormGroup {
    const controls: Record<string, unknown> = {};
    for (const field of this.formFields) {
      controls[field.field as string] = [null];
    }
    return this.#fb.group(controls);
  }

  loadData(e: TableLazyLoadEvent) {
    this.#lastLazyEvent = e;
    this.#updateActiveFilters(e);
    const q = query('');

    // Pagination
    if (typeof e.rows === 'number' && typeof e.first === 'number') {
      q.param('perPage', `${e.rows}`).page(e.first / e.rows + 1);
    }

    // Filter
    Object.entries(e.filters || {}).map(([field, filters]) => {
      if (filters) {
        const filterValues = (
          filters instanceof Array ? filters : [filters]
        ).reduce(
          (acc, x) => (x.value === null ? acc : [...acc, x.value]),
          [] as string[],
        );
        if (filterValues.length) {
          q.filter(field, filterValues.join(','));
        }
      }
    });

    // Sort
    if (e.multiSortMeta instanceof Array) {
      q.sort(
        ...e.multiSortMeta.map(({ field, order }) =>
          order < 1 ? `-${field}` : field,
        ),
      );
    }

    this.isLoading.set(true);
    this.#api
      .readMany(q.build())
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (result) => {
          const parsed: Record<string, unknown>[] = [];
          for (const user of result.data) {
            const parseResult = this.#schema.safeParse(user);
            if (parseResult.success) {
              parsed.push(parseResult.data as Record<string, unknown>);
            } else {
              console.error(parseResult.error);
            }
          }
          this.users.set(parsed);
          this.#collectFilterValues(parsed);
          this.totalRecords.set(result.meta.pagination.total);
        },
        error: (e: HttpErrorResponse) => {
          // TODO: implement signout in @fsf/auth
          if (e.status === 401) {
            localStorage.clear();
            this.#router.navigateByUrl('/');
          }
        },
      });
  }

  reloadData() {
    if (this.#lastLazyEvent) {
      this.loadData(this.#lastLazyEvent);
    }
  }

  onRowEditInit(user: Record<string, unknown>) {
    const patchData: Record<string, unknown> = {};
    for (const field of this.formFields) {
      let value = user[field.field as string];
      if (field.type === 'date' && typeof value === 'string') {
        value = new Date(value);
      }
      patchData[field.field as string] = value;
    }
    this.userForm.patchValue(patchData);
    this.currentUser = user;
    this.userPopupVisible = true;
  }

  onRowDeleteInit(event: Event, user: Record<string, unknown>) {
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.#translateService.instant('user.deleteConfirmMessage'),
      header: this.#translateService.instant('user.deleteHeader'),
      rejectButtonProps: {
        label: this.#translateService.instant('label.cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: this.#translateService.instant('label.delete'),
        severity: 'danger',
      },
      accept: () => {
        this.#api.removeOne(user['id'] as string).subscribe({
          next: () => {
            this.reloadData();
            this.#messageService.add({
              severity: 'success',
              summary: this.#translateService.instant(
                'label.successfullyDeletedHeader',
              ),
              detail: this.#translateService.instant(
                'label.successfullyDeleted',
              ),
            });
          },
          error: () => {
            this.#messageService.add({
              severity: 'error',
              summary: this.#translateService.instant('label.errorHeader'),
              detail: this.#translateService.instant('label.errorDeleting'),
            });
          },
        });
      },
    });
  }

  showUserPopup() {
    const defaults = this.#schema.meta()?.emptyRecord?.();
    this.userForm.reset(defaults);
    this.currentUser = null;
    this.userPopupVisible = true;
  }

  onSubmit() {
    if (!this.userForm.valid) {
      this.userForm.markAllAsTouched();
      return;
    }

    const formValue = { ...this.userForm.value };
    for (const key of Object.keys(formValue)) {
      if (formValue[key] instanceof Date) {
        const d = formValue[key] as Date;
        formValue[key] =
          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }
    }

    const name =
      `${formValue['firstName'] ?? ''} ${formValue['lastName'] ?? ''}`.trim();
    const base =
      this.currentUser ?? this.#schema.meta()?.emptyRecord?.() ?? {};
    const record = { ...base, ...formValue, name };
    const result = this.#schema.safeEncode(record);

    if (!result.success) {
      console.error('Validation error:', result.error);
      this.#messageService.add({
        severity: 'error',
        summary: this.#translateService.instant('label.errorHeader'),
        detail: this.#translateService.instant('label.invalidData'),
      });
      return;
    }

    const formFieldKeys = new Set(
      this.formFields.map((f) => f.field as string),
    );
    formFieldKeys.add('name');
    const data: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(result.data)) {
      if (key !== 'id' && formFieldKeys.has(key)) {
        data[key] = value;
      }
    }

    if (this.currentUser?.['id']) {
      this.#api.updateOne(this.currentUser['id'] as string, data).subscribe({
        next: () => {
          this.closeUserPopup();
          this.reloadData();
          this.#messageService.add({
            severity: 'success',
            summary: this.#translateService.instant('label.successHeader'),
            detail: this.#translateService.instant('user.updated'),
          });
        },
        error: () => {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translateService.instant('label.errorHeader'),
            detail: this.#translateService.instant('user.updateFailed'),
          });
        },
      });
    } else {
      this.#api.addOne(data).subscribe({
        next: () => {
          this.closeUserPopup();
          this.reloadData();
          this.#messageService.add({
            severity: 'success',
            summary: this.#translateService.instant('label.successHeader'),
            detail: this.#translateService.instant('user.created'),
          });
        },
        error: () => {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translateService.instant('label.errorHeader'),
            detail: this.#translateService.instant('user.createFailed'),
          });
        },
      });
    }
  }

  onSendOnboardingMail(user: Record<string, unknown>) {
    const email = user['email'] as string;
    if (!email) return;

    this.#http
      .post(`${this.#endpoint}/sendOnboardingMailToUser`, { email })
      .subscribe({
        next: () => {
          this.#messageService.add({
            severity: 'success',
            summary: this.#translateService.instant('label.successHeader'),
            detail: this.#translateService.instant('label.onboardingMailSent'),
          });
        },
        error: () => {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translateService.instant('label.errorHeader'),
            detail: this.#translateService.instant('label.onboardingMailError'),
          });
        },
      });
  }

  clear(table: Table) {
    table.clear();
    this.activeFilters.set([]);
  }

  removeFilter(table: Table, field: string) {
    const filterMeta = table.filters[field];
    if (filterMeta) {
      if (Array.isArray(filterMeta)) {
        filterMeta.forEach((f) => (f.value = null));
      } else {
        filterMeta.value = null;
      }
    }
    table._filter();
  }

  #updateActiveFilters(e: TableLazyLoadEvent) {
    const chips: { field: string; label: string; value: string }[] = [];
    const colMap = new Map(this.cols.map((c) => [c.filterField as string, c]));

    for (const [field, filters] of Object.entries(e.filters || {})) {
      if (!filters) continue;
      const filterArr = filters instanceof Array ? filters : [filters];
      for (const f of filterArr) {
        if (f.value === null || f.value === undefined || f.value === '')
          continue;
        const col = colMap.get(field);
        const label = col
          ? this.#translateService.instant(col.header as string)
          : field;
        const displayValue = Array.isArray(f.value)
          ? f.value.join(', ')
          : String(f.value);
        chips.push({ field, label, value: displayValue });
      }
    }
    this.activeFilters.set(chips);
  }

  onGlobalFilter(table: Table, event: Event) {
    table.filterGlobal((event.target as HTMLInputElement).value, 'contains');
  }

  closeUserPopup() {
    this.userPopupVisible = false;
    this.userForm.reset();
    this.currentUser = null;
  }

  #collectFilterValues(data: Record<string, unknown>[]) {
    this.#collectedValues.update((current) => {
      const updated = { ...current };
      for (const col of this.cols) {
        if (col.matchMode === 'in' && !col.textOptions?.length) {
          const field = col.field as string;
          const existing = new Set(updated[field] ?? []);
          for (const item of data) {
            const val = item[field];
            if (val) existing.add(String(val));
          }
          updated[field] = [...existing].sort();
        }
      }
      return updated;
    });
  }

  #loadTagOptions() {
    const endpoints = new Set<{ field: string; endpoint: string }>();
    for (const col of this.cols) {
      if (col.optionsEndpoint) {
        endpoints.add({
          field: col.field as string,
          endpoint: col.optionsEndpoint,
        });
      }
    }
    for (const field of this.formFields) {
      if (field.optionsEndpoint && field.type !== 'select') {
        endpoints.add({
          field: field.field as string,
          endpoint: field.optionsEndpoint,
        });
      }
    }

    const apiBase = this.#endpoint.substring(
      0,
      this.#endpoint.indexOf('/api/') + 4,
    );

    for (const { field, endpoint } of endpoints) {
      this.#http
        .get<LaravelResult<Record<string, unknown>[]>>(`${apiBase}/${endpoint}`)
        .subscribe({
          next: (result) => {
            const names = result.data.map((item) =>
              typeof item === 'string' ? item : String(item['name']),
            );
            this.tagOptions.update((opts) => ({
              ...opts,
              [field]: names,
            }));
          },
        });
    }
  }

  #loadSelectOptions() {
    const apiBase = this.#endpoint.substring(
      0,
      this.#endpoint.indexOf('/api/') + 4,
    );

    for (const field of this.formFields) {
      if (field.type === 'select' && field.optionsEndpoint) {
        this.#http
          .get<LaravelResult<Record<string, unknown>[]>>(
            `${apiBase}/${field.optionsEndpoint}`,
          )
          .subscribe({
            next: (result) => {
              const options = result.data.map((item) => ({
                label: String(item['name']),
                value: Number(item['id']),
              }));
              this.selectOptions.update((opts) => ({
                ...opts,
                [field.field as string]: options,
              }));
            },
          });
      }
    }
  }
}
